/**
 * Reconcile step: the file lined up against a game's plays, row by row.
 *
 * Every row starts on the server's suggestion (Hudl wins situation fields,
 * ours wins the rest; missing plays are inserted). The coach only looks at
 * what needs attention — conflicts and plays missing from the game — and can
 * flip a field, a row, a whole column, or every conflict at once. Keyboard:
 * j/k move, h = use Hudl, o = keep ours, i/s = insert/skip.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { ApiError } from '../../lib/api-error';
import type {
  HudlColumnOverride,
  HudlCommitResult,
  HudlFieldChoice,
  HudlImportPreview,
  HudlPlaySnapshot,
  HudlReconcileResult,
  HudlReconcileRow,
  HudlReconcileStatus,
  HudlRowAction,
  HudlRowDecision,
  HudlValueDecision,
} from '../../lib/api-types';

import { formatDownDistance, formatHudlYardLine, plural, toMessage } from './hudl-format';
import { gamePath } from './import-card';
import { NewOptionsPanel } from './new-options-panel';

type Filter = 'attention' | HudlReconcileStatus | 'all';

interface RowState {
  action: HudlRowAction;
  fields: Record<string, HudlFieldChoice>;
}

const STATUS_COPY: Record<HudlReconcileStatus, { label: string; chip: string; tone: string }> = {
  conflict: { label: 'Different', chip: 'Different', tone: 'conflict' },
  hudl_only: { label: 'Missing from game', chip: 'Missing from game', tone: 'missing' },
  fills: { label: 'Fills blanks', chip: 'Fills blanks', tone: 'fills' },
  matched: { label: 'Same', chip: 'Same', tone: 'matched' },
  ours_only: { label: 'Only in your game', chip: 'Only in your game', tone: 'ours' },
};

const FILTER_ORDER: HudlReconcileStatus[] = [
  'conflict',
  'hudl_only',
  'fills',
  'matched',
  'ours_only',
];

function initialState(row: HudlReconcileRow): RowState {
  const fields: Record<string, HudlFieldChoice> = {};
  for (const field of row.fields) if (field.differs) fields[field.field] = field.default;
  return { action: row.default_action, fields };
}

/** Row action that follows from the per-field choices of an update/keep row. */
function actionFromFields(
  row: HudlReconcileRow,
  fields: Record<string, HudlFieldChoice>
): HudlRowAction {
  if (row.status === 'hudl_only' || row.status === 'ours_only') return row.default_action;
  return Object.values(fields).some((choice) => choice === 'hudl') ? 'update' : 'keep';
}

/** The commit payload: one decision per row the coach could have changed. */
export function buildDecisions(
  rows: readonly HudlReconcileRow[],
  state: ReadonlyMap<string, RowState>
): HudlRowDecision[] {
  const decisions: HudlRowDecision[] = [];
  for (const row of rows) {
    if (row.status === 'matched' || row.status === 'ours_only') continue;
    const entry = state.get(row.row_id) ?? initialState(row);
    const decision: HudlRowDecision = { row_id: row.row_id, action: entry.action };
    if (Object.keys(entry.fields).length > 0 && row.status !== 'hudl_only') {
      decision.fields = { ...entry.fields };
    }
    decisions.push(decision);
  }
  return decisions;
}

function matchesFilter(row: HudlReconcileRow, filter: Filter): boolean {
  if (filter === 'all') return true;
  if (filter === 'attention') return row.status === 'conflict' || row.status === 'hudl_only';
  return row.status === filter;
}

function display(value: string | number | null): string {
  if (value === null || value === '') return '—';
  return String(value);
}

function snapshotLine(snapshot: HudlPlaySnapshot): string {
  const parts = [
    formatDownDistance(snapshot.down, snapshot.distance),
    formatHudlYardLine(snapshot.yard_line),
    snapshot.play_type_label,
    snapshot.result_label,
    snapshot.gain_loss === null
      ? null
      : `${snapshot.gain_loss > 0 ? '+' : ''}${snapshot.gain_loss}`,
  ].filter((part): part is string => part !== null && part !== '—' && part !== '');
  return parts.join(' · ');
}

const ODK_LABEL: Record<'O' | 'D' | 'K', string> = {
  O: 'Offense',
  D: 'Defense',
  K: 'Special teams',
};

export interface ReconcileViewProps {
  preview: HudlImportPreview;
  initial: HudlReconcileResult;
  overrides: HudlColumnOverride[];
  initialValueDecisions: Map<string, HudlValueDecision>;
  onBack(): void;
  onDone(): void;
}

export function ReconcileView({
  preview,
  initial,
  overrides,
  initialValueDecisions,
  onBack,
  onDone,
}: ReconcileViewProps): JSX.Element {
  const api = useApiClient();
  const { tenantSlug } = useSession();
  const [data, setData] = useState(initial);
  const [state, setState] = useState<Map<string, RowState>>(
    () => new Map(initial.rows.map((row) => [row.row_id, initialState(row)]))
  );
  const [valueDecisions, setValueDecisions] = useState(initialValueDecisions);
  const [filter, setFilter] = useState<Filter>(
    initial.counts.conflict + initial.counts.hudl_only > 0 ? 'attention' : 'all'
  );
  const [focusIndex, setFocusIndex] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [result, setResult] = useState<HudlCommitResult | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());

  const rows = data.rows;
  const visible = useMemo(() => rows.filter((row) => matchesFilter(row, filter)), [rows, filter]);
  const focused = visible[Math.min(focusIndex, visible.length - 1)] ?? null;

  const setRow = useCallback((row: HudlReconcileRow, update: (current: RowState) => RowState) => {
    setState((current) => {
      const next = new Map(current);
      next.set(row.row_id, update(current.get(row.row_id) ?? initialState(row)));
      return next;
    });
  }, []);

  const chooseField = useCallback(
    (row: HudlReconcileRow, field: string, choice: HudlFieldChoice) =>
      setRow(row, (current) => {
        const fields = { ...current.fields, [field]: choice };
        return { action: actionFromFields(row, fields), fields };
      }),
    [setRow]
  );

  const chooseRow = useCallback(
    (row: HudlReconcileRow, choice: HudlFieldChoice) =>
      setRow(row, (current) => {
        const fields = Object.fromEntries(Object.keys(current.fields).map((key) => [key, choice]));
        return { action: actionFromFields(row, fields), fields };
      }),
    [setRow]
  );

  const setAction = useCallback(
    (row: HudlReconcileRow, action: HudlRowAction) =>
      setRow(row, (current) => ({ ...current, action })),
    [setRow]
  );

  function bulk(choice: HudlFieldChoice, field?: string): void {
    setState((current) => {
      const next = new Map(current);
      for (const row of rows) {
        if (row.status !== 'conflict' && row.status !== 'fills') continue;
        const entry = current.get(row.row_id) ?? initialState(row);
        const fields = { ...entry.fields };
        let isTouched = false;
        for (const key of Object.keys(fields)) {
          if (field !== undefined && key !== field) continue;
          if (row.status === 'fills' && field === undefined) continue;
          fields[key] = choice;
          isTouched = true;
        }
        if (isTouched) next.set(row.row_id, { action: actionFromFields(row, fields), fields });
      }
      return next;
    });
  }

  function bulkMissing(action: 'insert' | 'skip'): void {
    setState((current) => {
      const next = new Map(current);
      for (const row of rows) {
        if (row.status !== 'hudl_only') continue;
        next.set(row.row_id, { ...(current.get(row.row_id) ?? initialState(row)), action });
      }
      return next;
    });
  }

  /** Fields that differ on more than one conflict row → per-field bulk buttons. */
  const bulkFields = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const row of rows) {
      if (row.status !== 'conflict') continue;
      for (const field of row.fields) {
        if (!field.differs || field.ours === null) continue;
        const entry = counts.get(field.field) ?? { label: field.label, count: 0 };
        entry.count += 1;
        counts.set(field.field, entry);
      }
    }
    return [...counts.entries()]
      .filter(([, entry]) => entry.count > 1)
      .sort((left, right) => right[1].count - left[1].count);
  }, [rows]);

  // Keyboard shortcuts — ignored while typing in a control.
  useEffect(() => {
    function onKey(keyEvent: KeyboardEvent): void {
      if (result !== null || keyEvent.metaKey || keyEvent.ctrlKey || keyEvent.altKey) return;
      const target = keyEvent.target as HTMLElement | null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      const key = keyEvent.key.toLowerCase();
      if (key === 'j' || key === 'k') {
        keyEvent.preventDefault();
        setFocusIndex((index) =>
          Math.max(0, Math.min(visible.length - 1, index + (key === 'j' ? 1 : -1)))
        );
        return;
      }
      if (focused === null) return;
      if (key === 'h' && focused.status !== 'hudl_only') chooseRow(focused, 'hudl');
      else if (key === 'o' && focused.status !== 'hudl_only') chooseRow(focused, 'ours');
      else if (key === 'i' && focused.status === 'hudl_only') setAction(focused, 'insert');
      else if (key === 's' && focused.status === 'hudl_only') setAction(focused, 'skip');
      else return;
      keyEvent.preventDefault();
    }
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [visible.length, focused, chooseRow, setAction, result]);

  useEffect(() => {
    if (focused === null) return;
    const element = rowRefs.current.get(focused.row_id);
    if (
      element &&
      typeof element.scrollIntoView === 'function' &&
      element !== document.activeElement
    ) {
      element.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);

  const decisions = buildDecisions(rows, state);
  const updates = decisions.filter((decision) => decision.action === 'update').length;
  const inserts = decisions.filter((decision) => decision.action === 'insert').length;

  async function apply(): Promise<void> {
    setIsApplying(true);
    setError(null);
    try {
      const committed = await api.commitHudlImport(preview.upload_id, {
        target: { kind: 'existing', event_id: data.event_id },
        decisions,
        value_actions: [...valueDecisions.values()],
        column_overrides: overrides.length === 0 ? undefined : overrides,
        reconcile_token: data.reconcile_token,
      });
      setResult(committed);
      onDone();
    } catch (caught) {
      if (ApiError.isApiError(caught) && caught.code === 'RECONCILE_STALE') {
        setIsStale(true);
      } else {
        setError(toMessage(caught, 'The changes were not saved. Please try again.'));
      }
    } finally {
      setIsApplying(false);
    }
  }

  async function recheck(): Promise<void> {
    setIsRechecking(true);
    setError(null);
    try {
      const fresh = await api.reconcileHudlImport(preview.upload_id, {
        event_id: data.event_id,
        column_overrides: overrides.length === 0 ? undefined : overrides,
      });
      setData(fresh);
      setState((current) => {
        const next = new Map<string, RowState>();
        for (const row of fresh.rows) {
          const previous = current.get(row.row_id);
          const isSameShape =
            previous !== undefined &&
            Object.keys(previous.fields).every((key) =>
              row.fields.some((field) => field.field === key && field.differs)
            );
          next.set(row.row_id, isSameShape ? previous : initialState(row));
        }
        return next;
      });
      setIsStale(false);
      setFocusIndex(0);
    } catch (caught) {
      setError(toMessage(caught, 'We could not re-check the game. Please try again.'));
    } finally {
      setIsRechecking(false);
    }
  }

  if (result !== null) {
    return (
      <section className="card hudl-result" data-testid="hudl-reconcile-done">
        <div className="hudl-done__icon" aria-hidden="true">
          ✓
        </div>
        <h2 role="status">{result.event_name} is up to date</h2>
        <ul className="hudl-result__counts">
          <li>
            <strong>{result.updated}</strong> plays updated
          </li>
          <li>
            <strong>{result.inserted}</strong> plays added
          </li>
          <li>
            <strong>{result.unchanged}</strong> unchanged
          </li>
          {result.skipped > 0 ? (
            <li>
              <strong>{result.skipped}</strong> skipped
            </li>
          ) : null}
          {result.options_created > 0 ? (
            <li>
              <strong>{result.options_created}</strong> new options
            </li>
          ) : null}
        </ul>
        <p className="muted">
          Earlier versions of changed plays are kept in each play&rsquo;s history.
        </p>
        <div className="hudl-done__actions">
          <Link className="btn btn--primary" to={gamePath(tenantSlug, result.event_id)}>
            Open the game
          </Link>
          <button type="button" className="btn btn--ghost" onClick={onBack}>
            Back to Hudl
          </button>
        </div>
      </section>
    );
  }

  const attention = data.counts.conflict + data.counts.hudl_only;

  return (
    <div className="hudl-rec" data-testid="hudl-reconcile">
      <div className="hudl-rec__intro">
        <div>
          <h2>Review import into {data.event_name}</h2>
          <p className="muted hudl-rec__file" title={preview.file_name}>
            {preview.file_name}
          </p>
          <p className="muted">
            We picked a side for every difference — Hudl for down, distance, yard line, hash, gain
            and quarter; your entries for everything else. Change anything that looks wrong, then
            apply.
          </p>
        </div>
      </div>

      <div className="hudl-rec__bar" data-testid="hudl-reconcile-bar">
        <label className="hudl-rec__filter-select">
          <span className="visually-hidden">Show</span>
          <select
            className="field__control"
            value={filter}
            onChange={(changeEvent) => {
              setFilter(changeEvent.target.value as Filter);
              setFocusIndex(0);
            }}
          >
            <option value="attention">Needs a look ({attention})</option>
            {FILTER_ORDER.map((status) => (
              <option key={status} value={status} disabled={data.counts[status] === 0}>
                {STATUS_COPY[status].chip} ({data.counts[status]})
              </option>
            ))}
            <option value="all">All plays ({rows.length})</option>
          </select>
        </label>
        <div className="hudl-rec__filters" role="group" aria-label="Show">
          <button
            type="button"
            className="hudl-filter hudl-filter--attention"
            aria-pressed={filter === 'attention'}
            onClick={() => {
              setFilter('attention');
              setFocusIndex(0);
            }}
          >
            Needs a look <span className="hudl-filter__n">{attention}</span>
          </button>
          {FILTER_ORDER.map((status) => (
            <button
              key={status}
              type="button"
              className={`hudl-filter hudl-filter--${STATUS_COPY[status].tone}`}
              aria-pressed={filter === status}
              onClick={() => {
                setFilter(status);
                setFocusIndex(0);
              }}
              disabled={data.counts[status] === 0}
            >
              {STATUS_COPY[status].chip}{' '}
              <span className="hudl-filter__n">{data.counts[status]}</span>
            </button>
          ))}
          <button
            type="button"
            className="hudl-filter"
            aria-pressed={filter === 'all'}
            onClick={() => {
              setFilter('all');
              setFocusIndex(0);
            }}
          >
            All <span className="hudl-filter__n">{rows.length}</span>
          </button>
        </div>
        <div className="hudl-rec__apply">
          <span className="hudl-rec__apply-summary" data-testid="hudl-apply-summary">
            {plural(updates, 'update')} · {plural(inserts, 'new play', 'new plays')}
          </span>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void apply()}
            disabled={isApplying || isStale}
            aria-busy={isApplying}
            data-testid="hudl-apply"
          >
            {isApplying ? <span className="hudl-spinner" aria-hidden="true" /> : null}
            {isApplying ? 'Applying…' : 'Apply changes'}
          </button>
        </div>
      </div>

      {isStale ? (
        <div
          className="banner banner--warning hudl-rec__stale"
          role="alert"
          data-testid="hudl-stale"
        >
          <span>
            Plays in {data.event_name} changed while you were reviewing (someone may be entering
            plays). Re-check to compare against the latest plays — your choices are kept where the
            rows still line up.
          </span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => void recheck()}
            disabled={isRechecking}
          >
            {isRechecking ? 'Re-checking…' : 'Re-check'}
          </button>
        </div>
      ) : null}
      {error === null ? null : (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}

      {data.counts.conflict + data.counts.hudl_only + data.counts.fills > 0 ? (
        <div className="hudl-bulk" aria-label="Change many rows at once" role="group">
          {data.counts.conflict > 0 ? (
            <>
              <span className="hudl-bulk__label">All differences:</span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => bulk('hudl')}
                aria-label="Use Hudl for all differences"
              >
                Use Hudl
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => bulk('ours')}
                aria-label="Keep ours for all differences"
              >
                Keep ours
              </button>
            </>
          ) : null}
          {data.counts.hudl_only > 0 ? (
            <>
              <span className="hudl-bulk__label">Missing plays:</span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => bulkMissing('insert')}
                aria-label="Add all missing plays"
              >
                Add all
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => bulkMissing('skip')}
                aria-label="Skip all missing plays"
              >
                Skip all
              </button>
            </>
          ) : null}
          {bulkFields.length > 0 ? (
            <details className="hudl-bulk__fields">
              <summary>By field</summary>
              <ul>
                {bulkFields.map(([field, entry]) => (
                  <li key={field}>
                    <span>
                      {entry.label} <span className="muted">({entry.count})</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => bulk('hudl', field)}
                    >
                      Use Hudl for all
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => bulk('ours', field)}
                    >
                      Keep ours for all
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <p className="hudl-keys muted" aria-hidden="true">
            <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>h</kbd> Hudl · <kbd>o</kbd> ours · <kbd>i</kbd>/
            <kbd>s</kbd> add/skip
          </p>
        </div>
      ) : null}

      <NewOptionsPanel
        values={preview.unknown_values}
        decisions={valueDecisions}
        onChange={setValueDecisions}
      />

      {visible.length === 0 ? (
        <p className="hudl-empty" data-testid="hudl-rec-empty">
          {filter === 'attention'
            ? 'Nothing needs a look — every play lines up. Apply to fill in the blanks from Hudl.'
            : 'No plays in this group.'}
        </p>
      ) : (
        <ol className="hudl-rows" aria-label="Plays">
          {visible.map((row, index) => (
            <ReconcileRowView
              key={row.row_id}
              row={row}
              state={state.get(row.row_id) ?? initialState(row)}
              isFocused={focused?.row_id === row.row_id}
              refCallback={(element) => {
                if (element) rowRefs.current.set(row.row_id, element);
                else rowRefs.current.delete(row.row_id);
              }}
              onFocus={() => setFocusIndex(index)}
              onField={(field, choice) => chooseField(row, field, choice)}
              onRow={(choice) => chooseRow(row, choice)}
              onAction={(action) => setAction(row, action)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

interface ReconcileRowViewProps {
  row: HudlReconcileRow;
  state: RowState;
  isFocused: boolean;
  refCallback(element: HTMLElement | null): void;
  onFocus(): void;
  onField(field: string, choice: HudlFieldChoice): void;
  onRow(choice: HudlFieldChoice): void;
  onAction(action: HudlRowAction): void;
}

function ReconcileRowView({
  row,
  state,
  isFocused,
  refCallback,
  onFocus,
  onField,
  onRow,
  onAction,
}: ReconcileRowViewProps): JSX.Element {
  const copy = STATUS_COPY[row.status];
  const snapshot = row.hudl ?? row.ours;
  const odk = snapshot?.odk ?? 'O';
  const differing = row.fields.filter((field) => field.differs);
  const allHudl =
    differing.length > 0 && differing.every((field) => state.fields[field.field] === 'hudl');
  const allOurs =
    differing.length > 0 && differing.every((field) => state.fields[field.field] === 'ours');
  const isSkipped = row.status === 'hudl_only' && state.action === 'skip';

  return (
    <li
      ref={refCallback}
      className={`hudl-row hudl-row--${copy.tone}${isFocused ? ' is-focused' : ''}${isSkipped ? ' is-skipped' : ''}`}
      data-row={row.row_id}
      data-status={row.status}
      onFocusCapture={onFocus}
      onClick={onFocus}
      tabIndex={-1}
    >
      <div className="hudl-row__head">
        <span className="hudl-row__pos" title="Position in the game">
          #{row.position}
        </span>
        <span className="hudl-row__meta">
          {snapshot?.quarter === null || snapshot === null ? '' : `Q${snapshot.quarter} · `}
          {ODK_LABEL[odk]}
          {row.ours === null ? '' : ` · Your play ${row.ours.play_number}`}
        </span>
        <span className={`hudl-status hudl-status--${copy.tone}`}>{copy.label}</span>
        <div className="hudl-row__quick">
          {row.status === 'hudl_only' ? (
            <div className="hudl-mini-seg" role="group" aria-label={`Play #${row.position}`}>
              <button
                type="button"
                aria-pressed={state.action === 'insert'}
                onClick={() => onAction('insert')}
              >
                Add to game
              </button>
              <button
                type="button"
                aria-pressed={state.action === 'skip'}
                onClick={() => onAction('skip')}
              >
                Skip
              </button>
            </div>
          ) : differing.length > 0 ? (
            <div className="hudl-mini-seg" role="group" aria-label={`Whole play #${row.position}`}>
              <button type="button" aria-pressed={allHudl} onClick={() => onRow('hudl')}>
                All Hudl
              </button>
              <button type="button" aria-pressed={allOurs} onClick={() => onRow('ours')}>
                All ours
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {row.status === 'hudl_only' && row.hudl !== null ? (
        <p className="hudl-row__summary">
          <span className="hudl-row__source">Hudl</span>
          {snapshotLine(row.hudl)}
          {row.hudl.summary === '' ? null : <span className="muted"> — {row.hudl.summary}</span>}
        </p>
      ) : null}
      {row.status === 'ours_only' && row.ours !== null ? (
        <p className="hudl-row__summary">
          <span className="hudl-row__source hudl-row__source--ours">Ours</span>
          {snapshotLine(row.ours)}
          <span className="muted"> — not in the file, kept as is</span>
        </p>
      ) : null}
      {row.status === 'matched' && snapshot !== null ? (
        <p className="hudl-row__summary">{snapshotLine(snapshot)}</p>
      ) : null}

      {differing.length > 0 && row.status !== 'hudl_only' ? (
        <div className="hudl-diff" role="table" aria-label={`Differences on play #${row.position}`}>
          <div className="hudl-diff__head" role="row">
            <span role="columnheader">Field</span>
            <span role="columnheader">Hudl</span>
            <span role="columnheader">Ours</span>
            <span role="columnheader" aria-label="Choice" />
          </div>
          {differing.map((field) => {
            const choice = state.fields[field.field] ?? field.default;
            return (
              <div key={field.field} className="hudl-diff__row" role="row" data-field={field.field}>
                <span role="cell" className="hudl-diff__label">
                  {field.label}
                </span>
                <span
                  role="cell"
                  className={`hudl-diff__val${choice === 'hudl' ? ' is-chosen' : ''}`}
                >
                  <span className="hudl-diff__src" aria-hidden="true">
                    Hudl
                  </span>
                  {display(field.hudl)}
                </span>
                <span
                  role="cell"
                  className={`hudl-diff__val${choice === 'ours' ? ' is-chosen' : ''}`}
                >
                  <span className="hudl-diff__src" aria-hidden="true">
                    Ours
                  </span>
                  {display(field.ours)}
                </span>
                <span role="cell" className="hudl-diff__pick">
                  <span
                    className="hudl-mini-seg"
                    role="group"
                    aria-label={`${field.label} on play #${row.position}`}
                  >
                    <button
                      type="button"
                      aria-pressed={choice === 'hudl'}
                      onClick={() => onField(field.field, 'hudl')}
                    >
                      Use Hudl
                    </button>
                    <button
                      type="button"
                      aria-pressed={choice === 'ours'}
                      onClick={() => onField(field.field, 'ours')}
                    >
                      Keep ours
                    </button>
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </li>
  );
}
